// src/hooks/usePlayProtectImage.ts
//
// iSentinel360 v0.4.5r2 — localized Play Protect warning image.
//
// The "App blocked to protect your device" warning that Android shows
// when sideloading is the same dialog in every language, but the text
// inside it is localized. The UpdateModal shows a preview of this
// dialog so users know what to expect. v0.4.5r2 makes the preview
// localized too.
//
// How to use: drop a screenshot of the Play Protect warning in each
// language at src/assets/play-protect-warning-{lang}.png. The hook
// returns the right one based on the active i18n language. If the
// current language doesn't have a screenshot, it falls back to English.
//
// Required assets (14 files for 14 supported languages — see i18n.ts):
//   src/assets/play-protect-warning-en.png  ← your existing screenshot, renamed
//   src/assets/play-protect-warning-es.png
//   src/assets/play-protect-warning-fr.png
//   src/assets/play-protect-warning-de.png
//   src/assets/play-protect-warning-it.png
//   src/assets/play-protect-warning-pt.png
//   src/assets/play-protect-warning-ru.png
//   src/assets/play-protect-warning-zh.png
//   src/assets/play-protect-warning-ja.png
//   src/assets/play-protect-warning-ko.png
//   src/assets/play-protect-warning-ar.png
//   src/assets/play-protect-warning-hi.png
//   src/assets/play-protect-warning-nl.png
//   src/assets/play-protect-warning-pl.png
//
// Naming note: I'm using `play-protect-warning-{lang}.png` to match
// the existing `play-protect-warning.png` filename. If you prefer
// the shorter `play-protect-{lang}.png` or the generic `image-{lang}.png`,
// just rename the assets and update the imports below.

import { useTranslation } from 'react-i18next';
import imgEn from '../assets/play-protect-warning-en.png';
import imgEs from '../assets/play-protect-warning-es.png';
import imgFr from '../assets/play-protect-warning-fr.png';
import imgDe from '../assets/play-protect-warning-de.png';
import imgIt from '../assets/play-protect-warning-it.png';
import imgPt from '../assets/play-protect-warning-pt.png';
import imgRu from '../assets/play-protect-warning-ru.png';
import imgZh from '../assets/play-protect-warning-zh.png';
import imgJa from '../assets/play-protect-warning-ja.png';
import imgKo from '../assets/play-protect-warning-ko.png';
import imgAr from '../assets/play-protect-warning-ar.png';
import imgHi from '../assets/play-protect-warning-hi.png';
import imgNl from '../assets/play-protect-warning-nl.png';
import imgPl from '../assets/play-protect-warning-pl.png';

const PLAY_PROTECT_IMAGES: Record<string, string> = {
  en: imgEn, es: imgEs, fr: imgFr, de: imgDe, it: imgIt, pt: imgPt,
  ru: imgRu, zh: imgZh, ja: imgJa, ko: imgKo, ar: imgAr, hi: imgHi,
  nl: imgNl, pl: imgPl,
};

export function usePlayProtectImage(): string {
  const { i18n } = useTranslation();
  // Fall back to English if the current language doesn't have a
  // screenshot. The supportedLngs list in i18n.ts covers all 14
  // languages; the fallback only triggers if the user adds a new
  // language later without providing a screenshot.
  return PLAY_PROTECT_IMAGES[i18n.language] ?? imgEn;
}
